import { absBig, BIG_ONE, formatBig } from "@/lib/tool-kit/math/bigint";
import { simplifySqrt } from "@/lib/tool-kit/math/radical";
import {
  absR,
  addR,
  formatRationalNice,
  fromBigInt,
  isZeroR,
  makeRational,
  mulR,
  type Rational,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Exact answers for geometry: sums of terms `coefficient · π^k · √radicand`.
 *
 * A cylinder of radius 2 and height 3 has volume 12π, a cone of radius 1 and height 1
 * has surface area π + π√2, and a regular hexagon of side 1 has area 3√3/2. Keeping
 * those as symbols — rational coefficient, a power of π, a square-free radicand — means
 * the exact form and the decimal can both be shown, and like terms combine on their own.
 */

export interface ExactTerm {
  coef: Rational;
  /** The power of π: 1 for π, 2 for π², −1 for 1/π. */
  pi: number;
  /** Square-free radicand: 1 for no root. */
  root: bigint;
}

export type Exact = readonly ExactTerm[];

export const EXACT_ZERO: Exact = [];

function termKey(term: ExactTerm): string {
  return `${term.pi}:${term.root.toString()}`;
}

function normalize(terms: readonly ExactTerm[]): Exact {
  const combined = new Map<string, ExactTerm>();
  for (const term of terms) {
    const key = termKey(term);
    const existing = combined.get(key);
    combined.set(
      key,
      existing ? { ...existing, coef: addR(existing.coef, term.coef) } : term
    );
  }
  return [...combined.values()]
    .filter((term) => !isZeroR(term.coef))
    .sort((a, b) => {
      if (a.pi !== b.pi) {
        return a.pi - b.pi;
      }
      return a.root < b.root ? -1 : Number(a.root > b.root);
    });
}

/** A plain rational number. */
export function exactRational(value: Rational): Exact {
  return normalize([{ coef: value, pi: 0, root: BIG_ONE }]);
}

/** value·π. */
export function exactPi(value: Rational): Exact {
  return normalize([{ coef: value, pi: 1, root: BIG_ONE }]);
}

/** value·π^power, for a circle's 1/π (the radius from a circumference) and the like. */
export function exactPiPower(value: Rational, power: number): Exact {
  return normalize([{ coef: value, pi: power, root: BIG_ONE }]);
}

/** √value for a non-negative rational, rationalised and in simplest form. */
export function exactSqrt(value: Rational): Exact {
  if (signR(value) <= 0) {
    return EXACT_ZERO;
  }
  const root = simplifySqrt(value.num * value.den);
  return normalize([
    {
      coef: makeRational(root.coefficient, value.den),
      pi: 0,
      root: root.radicand,
    },
  ]);
}

export function addExact(a: Exact, b: Exact): Exact {
  return normalize([...a, ...b]);
}

export function scaleExact(a: Exact, factor: Rational): Exact {
  return normalize(
    a.map((term) => ({ ...term, coef: mulR(term.coef, factor) }))
  );
}

function mulTerms(a: ExactTerm, b: ExactTerm): ExactTerm {
  const root = simplifySqrt(a.root * b.root);
  return {
    coef: mulR(mulR(a.coef, b.coef), fromBigInt(root.coefficient)),
    pi: a.pi + b.pi,
    root: root.radicand,
  };
}

export function mulExact(a: Exact, b: Exact): Exact {
  const products: ExactTerm[] = [];
  for (const left of a) {
    for (const right of b) {
      products.push(mulTerms(left, right));
    }
  }
  return normalize(products);
}

export function isZeroExact(a: Exact): boolean {
  return a.length === 0;
}

/** True when the value is a rational number: no π and no root anywhere. */
export function isRationalExact(a: Exact): boolean {
  return a.every((term) => term.pi === 0 && term.root === BIG_ONE);
}

export function toNumberExact(a: Exact): number {
  let total = 0;
  for (const term of a) {
    total +=
      toNumberR(term.coef) * Math.PI ** term.pi * Math.sqrt(Number(term.root));
  }
  return total;
}

const PI_SYMBOL = ["", "π", "π²", "π³"];

function piText(power: number): string {
  return PI_SYMBOL[Math.abs(power)] ?? `π^${Math.abs(power)}`;
}

function formatSymbols(term: ExactTerm): string {
  const pi = term.pi > 0 ? piText(term.pi) : "";
  const root = term.root === BIG_ONE ? "" : `√${term.root.toString()}`;
  return `${pi}${root}`;
}

/** How a value is written: fractions by default, terminating decimals on request. */
export interface ExactFormat {
  /** Write plain rational numbers as decimals where they terminate (7.5, not 15/2). */
  decimals?: boolean;
  mark?: "." | ",";
}

function formatTerm(term: ExactTerm, format: ExactFormat): string {
  if (format.decimals && term.pi === 0 && term.root === BIG_ONE) {
    return formatRationalNice(absR(term.coef), format.mark ?? ".");
  }
  const numerator = absBig(term.coef.num);
  const symbols = formatSymbols(term);
  const top =
    symbols !== "" && numerator === BIG_ONE ? "" : formatBig(numerator);
  const upper = `${top}${symbols}` || "1";
  // π in the denominator (a circle's radius from its circumference: 5/π).
  const piBelow = term.pi < 0 ? piText(term.pi) : "";
  const denominator = term.coef.den === BIG_ONE ? "" : term.coef.den.toString();
  const lower = `${denominator}${piBelow}`;
  if (lower === "") {
    return upper;
  }
  return denominator !== "" && piBelow !== ""
    ? `${upper}/(${lower})`
    : `${upper}/${lower}`;
}

function joinTerms(terms: readonly ExactTerm[], format: ExactFormat): string {
  let text = "";
  for (const [index, term] of terms.entries()) {
    const negative = signR(term.coef) < 0;
    if (index === 0) {
      text += negative ? "−" : "";
    } else {
      text += negative ? " − " : " + ";
    }
    text += formatTerm(term, format);
  }
  return text;
}

/** A positive term first, when there is one, so a sum does not open with a minus sign. */
function leadPositive(terms: Exact): Exact {
  if (terms.length < 2 || signR(terms[0].coef) > 0) {
    return terms;
  }
  const lead = terms.findIndex((term) => signR(term.coef) > 0);
  if (lead === -1) {
    return terms;
  }
  return [terms[lead], ...terms.filter((_, index) => index !== lead)];
}

/** (√6 − √2)/4: one denominator pulled out of a sum whose terms all share it. */
function commonDenominatorForm(
  terms: Exact,
  format: ExactFormat
): string | null {
  const denominator = terms[0].coef.den;
  const shared =
    terms.length > 1 &&
    denominator !== BIG_ONE &&
    terms.every((term) => term.coef.den === denominator && term.pi >= 0);
  if (!shared) {
    return null;
  }
  const numerators = terms.map((term) => ({
    ...term,
    coef: makeRational(term.coef.num),
  }));
  return `(${joinTerms(numerators, format)})/${denominator.toString()}`;
}

/** "32π/3", "π + π√2", "(√6 − √2)/4", "3√3/2", "0". */
export function formatExact(a: Exact, format: ExactFormat = {}): string {
  if (a.length === 0) {
    return "0";
  }
  const terms = leadPositive(a);
  return commonDenominatorForm(terms, format) ?? joinTerms(terms, format);
}

/** The exact form followed by a unit, bracketing a sum: "(π + π√2) cm²". */
export function formatExactWithUnit(
  a: Exact,
  unit: string,
  format: ExactFormat = {}
): string {
  const text = formatExact(a, format);
  return a.length > 1 ? `(${text}) ${unit}` : `${text} ${unit}`;
}
